import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Modal,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import districtMapPreview from "../../assests/images/district-8-map-preview.jpg";

const districtMapFull = process.env.PUBLIC_URL + "/district-8-map-full.png";
const MAX_ZOOM = 10;

const DistrictMap = () => {
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleOpen = () => {
    setOpen(true);
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleClose = () => setOpen(false);

  const handleZoomIn = () => setScale((s) => Math.min(s + 0.5, MAX_ZOOM));
  const handleZoomOut = () => {
    setScale((s) => {
      const newScale = Math.max(s - 0.5, 1);
      if (newScale === 1) setPosition({ x: 0, y: 0 });
      return newScale;
    });
  };

  const wheelRaf = useRef(null);
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (wheelRaf.current) return; // throttle to one update per animation frame
    wheelRaf.current = requestAnimationFrame(() => {
      wheelRaf.current = null;
      // Trackpad pinch sends ctrlKey + small deltaY; normal scroll sends larger deltaY
      const zoomStep = e.ctrlKey ? 0.05 : 0.15;
      if (e.deltaY < 0) {
        setScale((s) => Math.min(s + zoomStep, MAX_ZOOM));
      } else {
        setScale((s) => {
          const newScale = Math.max(s - zoomStep, 1);
          if (newScale === 1) setPosition({ x: 0, y: 0 });
          return newScale;
        });
      }
    });
  }, []);

  // Pointer-based panning (works for both mouse and touch)
  const handlePointerDown = useCallback(
    (e) => {
      if (scale <= 1) return;
      e.target.setPointerCapture(e.pointerId);
      setDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    },
    [scale, position],
  );

  const handlePointerMove = useCallback(
    (e) => {
      if (!dragging) return;
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    },
    [dragging, dragStart],
  );

  const handlePointerUp = useCallback(() => {
    setDragging(false);
  }, []);

  // Touch-based pinch zoom
  const [lastTouchDist, setLastTouchDist] = useState(null);

  const getTouchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      setLastTouchDist(getTouchDistance(e.touches));
    }
  }, []);

  const handleTouchMove = useCallback(
    (e) => {
      if (e.touches.length === 2 && lastTouchDist) {
        e.preventDefault();
        const dist = getTouchDistance(e.touches);
        const delta = dist - lastTouchDist;
        setScale((s) => Math.min(Math.max(s + delta * 0.01, 1), MAX_ZOOM));
        setLastTouchDist(dist);
      }
    },
    [lastTouchDist],
  );

  const handleTouchEnd = useCallback(() => {
    setLastTouchDist(null);
  }, []);

  // Attach wheel + touch listeners with { passive: false } so preventDefault works
  const zoomContainerRef = useRef(null);
  useEffect(() => {
    const el = zoomContainerRef.current;
    if (!open || !el) return;
    el.addEventListener("wheel", handleWheel, { passive: false });
    el.addEventListener("touchstart", handleTouchStart, { passive: false });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd, { passive: false });
    return () => {
      el.removeEventListener("wheel", handleWheel);
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
    };
  }, [open, handleWheel, handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Keyboard support for zoom
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") handleClose();
      if (e.key === "+" || e.key === "=") handleZoomIn();
      if (e.key === "-") handleZoomOut();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  return (
    <Box sx={{ bgcolor: "#fdfaf4", py: { xs: 8, md: 10 }, width: "100%" }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 6, maxWidth: "700px" }}>
          <Typography
            sx={{
              color: "#C59324",
              fontWeight: 700,
              letterSpacing: "0.2em",
              fontSize: "0.85rem",
              textTransform: "uppercase",
              mb: 2,
            }}
          >
            District 8 Map
          </Typography>

          <Typography
            sx={{
              mb: "8px",
              color: "#333",
              fontWeight: 700,
              fontFamily: "'Playfair Display', serif",
              mt: 1,
              fontSize: {
                xs: "2rem",
                sm: "2.5rem",
                md: "3rem",
              },
            }}
          >
            Are You in{" "}
            <Box
              component="span"
              sx={{
                color: "#C59324",
                fontFamily: "'Playfair Display', serif",
                fontStyle: "italic",
                fontWeight: 500,
              }}
            >
              Gary's District?
            </Box>
          </Typography>

          <Typography
            sx={{
              color: "#5D6785",
              fontSize: "0.95rem",
              lineHeight: 1.7,
              maxWidth: "550px",
            }}
          >
            Delaware House District 8 covers the Town limits of Middletown and
            surrounding New Castle County communities. Click the map below to
            enlarge it and zoom in to find your street.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "column", md: "row" },
            gap: { xs: 3, md: 6 },
            alignItems: "flex-start",
          }}
        >
          {/* Map preview image */}
          <Box
            sx={{
              flex: { xs: "none", md: 1.3 },
              width: "100%",
              position: "relative",
              borderRadius: "8px",
              overflow: "hidden",
              border: "1px solid rgba(0,0,0,0.1)",
              cursor: "pointer",
              "&:hover .map-overlay": {
                opacity: 1,
              },
            }}
            onClick={handleOpen}
          >
            <img
              src={districtMapPreview}
              alt="State Representative District 8 Map - Delaware Department of Elections, New Castle County"
              style={{
                width: "100%",
                height: "auto",
                display: "block",
              }}
            />

            {/* Hover overlay with prompt */}
            <Box
              className="map-overlay"
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(5, 19, 40, 0.5)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                opacity: { xs: 0, md: 0 },
                transition: "opacity 0.3s ease",
              }}
            >
              <ZoomInIcon sx={{ color: "#FFF", fontSize: "3rem", mb: 1 }} />
              <Typography
                sx={{
                  color: "#FFF",
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  textAlign: "center",
                }}
              >
                Click to enlarge map
              </Typography>
            </Box>

            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                bgcolor: "#051328",
                color: "#FFF",
                px: 2,
                py: 1,
                fontSize: "0.75rem",
                fontWeight: 700,
                letterSpacing: "1px",
              }}
            >
              DELAWARE HOUSE DISTRICT 8
            </Box>

            {/* Mobile tap hint */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                position: "absolute",
                top: 12,
                right: 12,
                bgcolor: "#051328",
                color: "#FFF",
                px: 1.5,
                py: 0.5,
                borderRadius: "4px",
                fontSize: "0.7rem",
                fontWeight: 600,
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <ZoomInIcon sx={{ fontSize: "0.9rem" }} />
              Tap to enlarge
            </Box>
          </Box>

          {/* Side info panel */}
          <Box sx={{ flex: { xs: "none", md: 0.7 }, width: "100%" }}>
            <Typography
              sx={{
                color: "#051328",
                fontWeight: 700,
                fontSize: "1.1rem",
                mb: 3,
              }}
            >
              Communities in District 8
            </Typography>

            <Box sx={{ display: "flex", gap: 1.5, mb: 4 }}>
              <Box
                sx={{
                  minWidth: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  bgcolor: "#C59324",
                  mt: 1,
                  flexShrink: 0,
                }}
              />
              <Box>
                <Typography
                  sx={{ fontWeight: 800, fontSize: "0.9rem", color: "#051328" }}
                >
                  Town limits of Middletown
                </Typography>
                <Typography sx={{ fontSize: "0.85rem", color: "#5D6785" }}>
                  and surrounding New Castle County neighborhoods.
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                bgcolor: "#051328",
                borderRadius: "8px",
                p: 3,
                color: "#FFF",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                How to check your district
              </Typography>

              <Typography
                sx={{ fontSize: "0.8rem", opacity: 0.85, lineHeight: 1.7 }}
              >
                Click the map to open a full-size view. Use the zoom controls or
                scroll to zoom in and find your street. Close the map when you're
                done to return to this page.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Full-screen zoom modal */}
      <Modal
        open={open}
        onClose={handleClose}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2100,
        }}
      >
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0, 0, 0, 0.95)",
            display: "flex",
            flexDirection: "column",
            outline: "none",
          }}
        >
          {/* Modal header — stays on top, never zooms */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: { xs: 2, md: 3 },
              py: 1.5,
              bgcolor: "#051328",
              flexShrink: 0,
              position: "relative",
              zIndex: 10,
            }}
          >
            <Typography
              sx={{
                color: "#FFF",
                fontWeight: 700,
                fontSize: { xs: "0.85rem", md: "1rem" },
              }}
            >
              District 8 Map — Scroll or pinch to zoom, drag to pan
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={handleZoomOut}
                sx={{ color: "#FFF" }}
                size="small"
              >
                <ZoomOutIcon />
              </IconButton>
              <Typography
                sx={{
                  color: "#C59324",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  minWidth: "40px",
                  textAlign: "center",
                }}
              >
                {Math.round(scale * 100)}%
              </Typography>
              <IconButton
                onClick={handleZoomIn}
                sx={{ color: "#FFF" }}
                size="small"
              >
                <ZoomInIcon />
              </IconButton>
              <IconButton
                onClick={handleClose}
                sx={{ color: "#FFF", ml: 1 }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Zoomable image container — clips zoomed content */}
          <Box
            ref={zoomContainerRef}
            sx={{
              flex: 1,
              position: "relative",
              overflow: "hidden",
              cursor: scale > 1 ? "grab" : "zoom-in",
              touchAction: "none",
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onClick={(e) => {
              if (!dragging && scale === 1) handleZoomIn();
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                transformOrigin: "center center",
                transition: dragging ? "none" : "transform 0.2s ease",
              }}
            >
              <img
                src={districtMapFull}
                alt="State Representative District 8 Map - Full Resolution"
                draggable={false}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  userSelect: "none",
                }}
              />
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default DistrictMap;
